"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.askedQuestionSchema = void 0;
exports.askedQuestionsOfOutput = askedQuestionsOfOutput;
exports.answerText = answerText;
exports.isAskUser = isAskUser;
exports.questionsOfCall = questionsOfCall;
exports.askedQuestionsIn = askedQuestionsIn;
exports.answeredQuestions = answeredQuestions;
exports.questionsAskedBy = questionsAskedBy;
exports.askedAt = askedAt;
exports.interleaveAsked = interleaveAsked;
exports.closeOpenQuestions = closeOpenQuestions;
const zod_1 = require("zod");
const questions_1 = require("./questions");
/**
 * A question the agent asked in a reply, with what the person answered, if they did, as a stored
 * reply keeps it. Parse with it to read one back from unchecked JSON.
 */
exports.askedQuestionSchema = zod_1.z.object({
    question: zod_1.z.string(),
    answer: zod_1.z.string().optional(),
    /**
     * How far into the reply's text it was asked, in characters. Absent on replies stored before
     * this was recorded, which read as asked before the text.
     */
    at: zod_1.z.number().int().nonnegative().optional(),
    /**
     * The call that asked it: an ask_user call, or a tool that asked about its own call. What one
     * call asked is answered, and shown, together.
     */
    callId: zod_1.z.string().optional(),
});
const askedQuestionsSchema = zod_1.z.array(exports.askedQuestionSchema.pick({ question: true, answer: true }));
/**
 * What a tool that asked about its own call says it asked, from its output's `askedQuestions`. The
 * ask_user tool's questions are its input instead.
 */
function askedQuestionsOfOutput(output) {
    const asked = output?.askedQuestions;
    if (!Array.isArray(asked) || asked.length === 0)
        return undefined;
    const parsed = askedQuestionsSchema.safeParse(asked);
    return parsed.success ? parsed.data : undefined;
}
function answerText(answer) {
    return [...answer.selected, ...(answer.other ? [answer.other] : [])].join(', ');
}
/** Whether a thread part is an ask_user call, typed or known to the client only by name. */
function isAskUser(part) {
    const call = part;
    return (call.type === `tool-${questions_1.ASK_USER_TOOL_NAME}` ||
        (call.type === 'dynamic-tool' && call.toolName === questions_1.ASK_USER_TOOL_NAME));
}
/**
 * The questions a pending call asks, when it is a question. By input as well as name: a connected
 * server can offer a tool called ask_user too, and one whose input does not read as questions stays
 * an ordinary approval, which can still be denied.
 */
function questionsOfCall(toolName, input) {
    if (toolName !== questions_1.ASK_USER_TOOL_NAME)
        return undefined;
    const parsed = questions_1.askUserInputSchema.safeParse(input);
    return parsed.success ? parsed.data.questions : undefined;
}
/**
 * What a finished ask_user call in a thread asked and what came back: the answers once it
 * completes, the bare questions when the person skipped them or moved on. Nothing while the call
 * still waits, since its questions are on screen to answer.
 */
function askedQuestionsIn(part) {
    const call = part;
    const callId = typeof call.toolCallId === 'string' ? call.toolCallId : undefined;
    if (!isAskUser(call)) {
        if (call.state !== 'output-available')
            return undefined;
        const own = askedQuestionsOfOutput(call.output);
        return own && askedBy(own, callId);
    }
    if (call.state === 'output-available')
        return answeredQuestions(call.output, callId);
    if (call.state !== 'output-error' && call.state !== 'output-denied')
        return undefined;
    return questionsAskedBy(call.input, callId);
}
/** What an ask_user call's result answered, one entry per question. */
function answeredQuestions(output, callId) {
    const parsed = questions_1.askUserAnswersSchema.safeParse(output);
    if (!parsed.success)
        return undefined;
    const answered = parsed.data.answers.map((answer) => ({
        question: answer.question,
        answer: answerText(answer),
    }));
    return askedBy(answered, callId);
}
/** The questions an ask_user call's input asks, none of them answered. */
function questionsAskedBy(input, callId) {
    const parsed = questions_1.askUserInputSchema.safeParse(input);
    if (!parsed.success)
        return undefined;
    const questions = parsed.data.questions.map(({ question }) => ({ question }));
    return askedBy(questions, callId);
}
const askedBy = (questions, callId) => callId ? questions.map((question) => ({ ...question, callId })) : questions;
/** The questions, marked as asked once the reply's text had reached this length. */
function askedAt(questions, at) {
    return questions.map((question) => ({ ...question, at }));
}
/**
 * A reply's text with its questions placed where they were asked, so each time the agent asked
 * reads as its own exchange rather than all of them in one block. Questions with no recorded
 * place go before the text, and one call's questions stay together.
 */
function interleaveAsked(text, asked) {
    const segments = [];
    let from = 0;
    for (const question of asked) {
        const at = Math.min(Math.max(question.at ?? 0, from), text.length);
        if (at > from) {
            const between = text.slice(from, at);
            if (between.trim() !== '')
                segments.push({ text: between });
            from = at;
        }
        const last = segments.at(-1);
        if (last && 'asked' in last && last.asked[0]?.callId === question.callId) {
            last.asked.push(question);
        }
        else {
            segments.push({ asked: [question] });
        }
    }
    const rest = text.slice(from);
    if (rest.trim() !== '')
        segments.push({ text: rest });
    return segments;
}
/**
 * The thread with every ask_user call still waiting closed as not answered, for a person who sent
 * a new message instead. Closed as an error rather than a declined approval: the SDK acts on an
 * approval response in a resent transcript as if it were new, streaming an outcome for a call the
 * new reply never made. A host that knows other calls waited on questions, such as a tool that
 * asked about its own call, says which with `asks`.
 */
function closeOpenQuestions(messages, asks = isAskUser) {
    const isOpen = (part) => asks(part) && part.state === 'approval-requested';
    return messages.map((message) => {
        if (!message.parts.some(isOpen))
            return message;
        return {
            ...message,
            parts: message.parts.map((part) => {
                if (!isOpen(part))
                    return part;
                const closed = {
                    ...part,
                    state: 'output-error',
                    errorText: questions_1.NOT_ANSWERED_REASON,
                };
                delete closed.approval;
                return closed;
            }),
        };
    });
}
